import { useState } from 'react';
import { Space, Input, Select, Table, Tag, Button, message } from 'antd';
import { TagsOutlined, PlusOutlined, FileExcelOutlined } from '@ant-design/icons';
import dayjs from 'libs/dayjs';
import { PageHeader } from 'components/headings';
import DateRangeFilter from 'components/filters/DateRangeFilter';
import useTicketsList from 'hooks/useTicketsList';
import TicketsKpiStrip from './TicketsKpiStrip';
import TicketFormModal from './TicketFormModal';
import { updateTicket, exportTicketsXlsx } from 'api/tickets';

const ESTADO_COLOR = { nuevo: 'blue', en_proceso: 'gold', resuelto: 'green', cerrado: 'default' };
const PRIORIDAD_COLOR = { baja: 'default', media: 'blue', alta: 'orange', urgente: 'red' };

export default function TicketsPage() {
  const {
    rows,
    total,
    loading,
    page,
    setPage,
    pageSize,
    search,
    setSearch,
    filters,
    setFilters,
    refresh,
  } = useTicketsList();
  const [reloadKey, setReloadKey] = useState(0);
  const [formOpen, setFormOpen] = useState(false);

  const reloadAll = () => {
    refresh();
    setReloadKey((k) => k + 1);
  };

  const setFilter = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const changeEstado = async (id, estado) => {
    try {
      await updateTicket(id, { estado });
      reloadAll();
    } catch (e) {
      message.error(e?.message || 'No se pudo actualizar el estado');
    }
  };

  const changePrioridad = async (id, prioridad) => {
    try {
      await updateTicket(id, { prioridad });
      reloadAll();
    } catch (e) {
      message.error(e?.message || 'No se pudo actualizar la prioridad');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: 'Estado',
      dataIndex: 'estado',
      render: (e) => <Tag color={ESTADO_COLOR[e]}>{e}</Tag>,
    },
    {
      title: 'Prioridad',
      dataIndex: 'prioridad',
      render: (p) => <Tag color={PRIORIDAD_COLOR[p]}>{p}</Tag>,
    },
    { title: 'Descripción', dataIndex: 'descripcion', ellipsis: true },
    { title: 'Origen', dataIndex: 'origen', width: 110 },
    {
      title: 'Creado',
      dataIndex: 'createdAt',
      width: 160,
      render: (d) => (d ? dayjs(d).format('DD-MM-YYYY HH:mm') : '—'),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 260,
      render: (_, row) => (
        <Space>
          <Select
            size="small"
            value={row.estado}
            style={{ width: 120 }}
            onChange={(v) => changeEstado(row.id, v)}
            options={['nuevo', 'en_proceso', 'resuelto', 'cerrado'].map((e) => ({
              value: e,
              label: e,
            }))}
          />
          <Select
            size="small"
            value={row.prioridad}
            style={{ width: 110 }}
            onChange={(v) => changePrioridad(row.id, v)}
            options={['baja', 'media', 'alta', 'urgente'].map((p) => ({
              value: p,
              label: p,
            }))}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Tickets"
        subtitle="Incidencias y solicitudes"
        icon={TagsOutlined}
        actions={
          <Space>
            <Button
              icon={<FileExcelOutlined />}
              onClick={() => exportTicketsXlsx({ ...filters, search })}
            >
              Exportar Excel
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormOpen(true)}>
              Nuevo ticket
            </Button>
          </Space>
        }
      />

      <TicketsKpiStrip
        filters={{ ...filters, search }}
        reloadKey={reloadKey}
        activeEstado={filters.estado}
        onToggleEstado={(estado) => setFilter({ estado })}
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Buscar en descripción"
          allowClear
          onSearch={(v) => {
            setSearch(v);
            setPage(1);
          }}
          style={{ width: 240 }}
        />
        <Select
          placeholder="Prioridad"
          allowClear
          style={{ width: 140 }}
          value={filters.prioridad}
          onChange={(prioridad) => setFilter({ prioridad })}
          options={['baja', 'media', 'alta', 'urgente'].map((p) => ({ value: p, label: p }))}
        />
        <DateRangeFilter onChange={(dateFrom, dateTo) => setFilter({ dateFrom, dateTo })} />
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ current: page, pageSize, total, onChange: setPage, showSizeChanger: false }}
      />

      <TicketFormModal open={formOpen} onClose={() => setFormOpen(false)} onCreated={reloadAll} />
    </div>
  );
}
